import { DRAFT_MESSAGES, store } from "../store";
import { stripMarkdown } from "./conversationUtils";

const getDraftsFromStore = () => {
  const drafts = store.getState()?.draftMessages;
  return Array.isArray(drafts) ? drafts : [];
};

const writeDrafts = (updated) => {
  try {
    localStorage.setItem("draftMessage", JSON.stringify(updated));
  } catch (e) {
    // Ignore quota / private-mode failures
  }
  store.dispatch({ type: DRAFT_MESSAGES, draftMessages: updated });
};

export const persistDraftMessage = (userId, groupId, text) => {
  if (!userId || !groupId) return;
  const trimmed = (text != null ? String(text) : "").trim();
  const current = getDraftsFromStore();
  let updated;
  if (!trimmed) {
    updated = current.filter((m) => !(m.userId === userId && m.group === groupId));
    if (updated.length === current.length) return;
  } else {
    const msgObject = {
      userId,
      message: trimmed,
      messageType: "simple",
      group: groupId,
    };
    const foundIndex = current.findIndex(
      (m) => m.userId === userId && m.group === groupId
    );
    if (foundIndex > -1) {
      if (current[foundIndex].message === trimmed) return;
      updated = current.map((m, i) =>
        i === foundIndex ? { ...m, message: trimmed } : m
      );
    } else {
      updated = [...current, msgObject];
    }
  }
  writeDrafts(updated);
};

export const clearStoredDraft = (groupId) => {
  if (!groupId) return;
  const current = getDraftsFromStore();
  const updated = current.filter((m) => m.group !== groupId);
  if (updated.length === current.length) return;
  writeDrafts(updated);
};

export const findDraftForChat = (drafts, userId, groupIds) => {
  const ids = (Array.isArray(groupIds) ? groupIds : [groupIds]).filter(Boolean);
  if (!userId || ids.length === 0) return null;
  const idSet = new Set(ids);
  return (drafts || []).find(
    (m) => m.userId === userId && idSet.has(m.group) && (m.message || "").trim()
  ) || null;
};

export const getDraftPreviewText = (message) => {
  if (!message) return "";
  return stripMarkdown(String(message))
    .replace(/@\[([^\]]*)\]\([^)]*\)/g, "@$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/<br\s*[/]?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export const readComposerDraftText = ({
  richEditorRef,
  getRichEditorContent,
  messageRef,
  isComposerToolbarOpen,
}) => {
  if (isComposerToolbarOpen && typeof getRichEditorContent === "function") {
    try {
      const fromEditor = getRichEditorContent();
      if (fromEditor && String(fromEditor).trim()) {
        return String(fromEditor).trim();
      }
    } catch (e) {
      // Fall through to React state
    }
  }
  if (richEditorRef?.current?.innerText) {
    const plain = String(richEditorRef.current.innerText).trim();
    if (plain) return plain;
  }
  return (messageRef?.current != null ? String(messageRef.current) : "").trim();
};

// WYSIWYG typing only syncs empty ↔ non-empty, so `message` is often stale.
// Call this before opening the media picker so the caption gets the live text.
export const flushComposerMessage = ({
  setMessage,
  messageRef,
  richEditorRef,
  getRichEditorContent,
  isComposerToolbarOpen,
}) => {
  const live = readComposerDraftText({
    richEditorRef,
    getRichEditorContent,
    messageRef,
    isComposerToolbarOpen,
  });
  if (messageRef) messageRef.current = live;
  if (typeof setMessage === "function") setMessage(live);
  return live;
};

export const applyDraftToComposer = ({
  draftText,
  setMessage,
  messageRef,
  richEditorRef,
  isComposerToolbarOpen,
  composerMarkdownToHtml,
  pendingDraftHtmlRef,
}) => {
  const text = draftText != null ? String(draftText) : "";
  if (messageRef) messageRef.current = text;
  setMessage(text);
  const html =
    text && typeof composerMarkdownToHtml === "function"
      ? composerMarkdownToHtml(text)
      : text;
  if (isComposerToolbarOpen && richEditorRef?.current) {
    richEditorRef.current.innerHTML = html;
    if (pendingDraftHtmlRef) pendingDraftHtmlRef.current = null;
  } else if (pendingDraftHtmlRef) {
    pendingDraftHtmlRef.current = html;
  }
};
